/**
 * <most-loved-slider> — Embla-powered product carousel (loop, centered) with
 * prev/next controls and a progress bar.
 *
 * Embla loads once per page via the shared singleton loader
 * (embla-loader.js -> window.oilogicEmbla). No Swiper. Progressive enhancement:
 * without Embla the track is a native horizontal scroller (see CSS).
 */
class MostLovedSlider extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-most-loved-viewport]');
    this.fill = this.querySelector('[data-most-loved-progress]');
    this.prevBtn = this.querySelector('[data-most-loved-prev]');
    this.nextBtn = this.querySelector('[data-most-loved-next]');
    if (!this.viewport) return;

    if (typeof window.EmblaCarousel === 'function') {
      this._init();
    } else if (typeof window.oilogicEmbla === 'function') {
      window.oilogicEmbla(this.dataset.emblaUrl)
        .then(() => this._init())
        .catch(() => {});
    }
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    this.embla?.destroy();
    this.embla = null;
  }

  _init() {
    if (this.embla || typeof window.EmblaCarousel !== 'function' || !this.isConnected) return;

    this.controller = new AbortController();
    const { signal } = this.controller;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.embla = window.EmblaCarousel(this.viewport, {
      loop: true,
      align: 'center',
      dragFree: false,
      duration: prefersReducedMotion ? 0 : 25,
    });

    this.prevBtn?.addEventListener('click', () => this.embla.scrollPrev(), { signal });
    this.nextBtn?.addEventListener('click', () => this.embla.scrollNext(), { signal });

    // :active alone is unreliable on hybrid touch+mouse devices (some never
    // fire it for touch). Pointer events give a consistent pressed state
    // across mouse, touch and pen.
    [this.prevBtn, this.nextBtn].forEach((btn) => {
      if (!btn) return;
      const press = () => btn.classList.add('is-pressed');
      const release = () => btn.classList.remove('is-pressed');
      btn.addEventListener('pointerdown', press, { signal });
      btn.addEventListener('pointerup', release, { signal });
      btn.addEventListener('pointercancel', release, { signal });
      btn.addEventListener('pointerleave', release, { signal });
    });

    this.embla.on('select', () => this._syncProgress());
    this.embla.on('reInit', () => this._syncProgress());
    this._syncProgress();
  }

  _syncProgress() {
    if (!this.fill || !this.embla) return;
    const snaps = this.embla.scrollSnapList().length;
    if (snaps < 2) return;
    const pct = Math.round((this.embla.selectedScrollSnap() / (snaps - 1)) * 100);
    this.fill.style.width = `${Math.max(0, Math.min(100, pct))}%`;
  }
}

customElements.define('most-loved-slider', MostLovedSlider);
